/**
 * nexus-agents/mcp - Run Workflow Tool
 *
 * MCP tool for executing workflow templates with the workflow engine.
 * Supports both built-in templates and custom template paths.
 *
 * @module mcp/tools/run-workflow
 * (Refactored: Issue #531 - Use createSecureHandlerFactory)
 */

import type { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import type { Result } from '../../core/index.js';
import {
  getErrorMessage,
  WorkflowError,
  ParseError,
  createLogger,
  getTimeProvider,
} from '../../core/index.js';

import type { WorkflowDefinition, IWorkflowEngine } from '../../core/index.js';
import {
  wrapToolWithTimeout,
  toSdkCallbackWithBudgetCheck,
  getToolTimeout,
} from '../middleware/tool-wrapper.js';
import { createSecureHandler, type HandlerContext } from '../middleware/secure-handler.js';
import { createMcpNotifier, NOOP_NOTIFIER } from '../mcp-notifier.js';
import type {
  RunWorkflowInput,
  WorkflowToolResult,
  RunWorkflowDeps,
} from './run-workflow-types.js';
import { RunWorkflowInputSchema } from './run-workflow-types.js';
import {
  type ToolResponse,
  loadWorkflow,
  validateWorkflowInputs,
  executeDryRun,
  deriveWorkflowStatus,
  toStepResultSummary,
  successResponse,
  errorResponse,
  createFailedResult,
  formatValidationErrors,
} from './run-workflow-helpers.js';
import { getToolMemory } from './tool-memory.js';
import { getToolAnnotations } from '../tool-annotations.js';
// #3044 / epic #2631 Stage 3 — async-mode dispatch via the shared `runAsJob`
// helper (#3729).
import { runAsJob } from '../jobs/run-as-job.js';
import { heartbeatJob } from '../jobs/job-result-store.js';
import { deprecatedModeWarning, resolveDispatch, withWarnings } from './async-dispatch-input.js';
import { randomUUID } from 'node:crypto';

// Re-export types for backward compatibility
export type {
  RunWorkflowInput,
  WorkflowToolResult,
  StepResultSummary,
  DryRunResult,
  RunWorkflowDeps,
} from './run-workflow-types.js';
export { RunWorkflowInputSchema } from './run-workflow-types.js';

// Re-export helper functions
export {
  isFilePath,
  toStepResultSummary,
  validateInputType,
  validateWorkflowInputs,
  loadWorkflow,
  executeDryRun,
  successResponse,
  errorResponse,
  createFailedResult,
  formatValidationErrors,
} from './run-workflow-helpers.js';
export type { ToolResponse, InputValidationResult } from './run-workflow-helpers.js';

/**
 * Resolves the EXECUTING engine, converting the #507 fail-safe into a Result (#5116).
 *
 * Extracted as its own function because it is a distinct responsibility —
 * turning a construction-time throw into a caller-facing error — not because
 * `executeWorkflow` was over a line cap. #5129 records that this repo has 46
 * helper files imported only by their own parent, created to satisfy that cap
 * rather than to separate anything.
 */
function resolveExecutionEngineOrError(
  deps: RunWorkflowDeps,
  workflowName: string
): Result<IWorkflowEngine, WorkflowError> {
  try {
    // Falls back to the caller's own engine when unset — correct for an
    // external caller whose single engine serves both listing and execution
    // (unanimous panel, PR #5133). The production registration always supplies
    // it, asserted in cli-server-tools-workflow-seam.test.ts.
    const resolve = deps.resolveExecutionEngine ?? ((): IWorkflowEngine => deps.workflowEngine);
    return { ok: true, value: resolve() };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    deps.logger?.error(
      'Workflow execution unavailable',
      error instanceof Error ? error : undefined,
      { workflowName }
    );
    return {
      ok: false,
      error: new WorkflowError(`Cannot execute workflow "${workflowName}": ${message}`),
    };
  }
}

/**
 * Execute workflow and convert result.
 *
 * @param deps - Tool dependencies
 * @param workflow - Workflow definition
 * @param inputs - Workflow inputs
 * @returns Tool result
 */
/** Per-run engine overrides: the phase timeout (#3017) and the heartbeat (#6162). */
interface EngineRunOptions {
  readonly phaseTimeoutMs?: number;
  readonly onPhaseComplete?: () => void;
}

/**
 * Run the engine, passing the third arg only when an option is set — keeps the
 * `(workflow, inputs)` call shape for existing tests that
 * toHaveBeenCalledWith exactly two args (vitest treats explicit `undefined`
 * as a third arg).
 */
function runEngine(
  engine: IWorkflowEngine,
  workflow: WorkflowDefinition,
  inputs: Record<string, unknown>,
  options: EngineRunOptions | undefined
): ReturnType<IWorkflowEngine['execute']> {
  const engineOptions = {
    ...(options?.phaseTimeoutMs !== undefined ? { phaseTimeoutMs: options.phaseTimeoutMs } : {}),
    ...(options?.onPhaseComplete !== undefined ? { onPhaseComplete: options.onPhaseComplete } : {}),
  };
  return Object.keys(engineOptions).length > 0
    ? engine.execute(workflow, inputs, engineOptions)
    : engine.execute(workflow, inputs);
}

async function executeWorkflow(
  deps: RunWorkflowDeps,
  workflow: WorkflowDefinition,
  inputs: Record<string, unknown>,
  options?: EngineRunOptions
): Promise<Result<WorkflowToolResult, WorkflowError>> {
  const { logger } = deps;

  const engineOrError = resolveExecutionEngineOrError(deps, workflow.name);
  if (!engineOrError.ok) return engineOrError;

  logger?.info('Executing workflow', {
    workflowName: workflow.name,
    inputCount: Object.keys(inputs).length,
    ...(options?.phaseTimeoutMs !== undefined ? { phaseTimeoutMs: options.phaseTimeoutMs } : {}),
  });

  const startTime = getTimeProvider().now();
  const result = await runEngine(engineOrError.value, workflow, inputs, options);

  if (!result.ok) {
    logger?.error('Workflow execution failed', result.error, {
      workflowName: workflow.name,
    });

    return {
      ok: false,
      error: result.error,
    };
  }

  const workflowResult = result.value;
  const stepResults = workflowResult.stepResults.map(toStepResultSummary);
  const status = deriveWorkflowStatus(stepResults);

  logger?.info('Workflow execution finished', {
    workflowName: workflow.name,
    durationMs: getTimeProvider().now() - startTime,
    stepCount: workflowResult.stepResults.length,
    status,
  });

  return {
    ok: true,
    value: {
      executionId: workflowResult.executionId,
      workflowName: workflowResult.workflowName,
      // #4351: was hardcoded 'completed'. A run whose steps all failed —
      // e.g. every adapter out of capacity — reported success to the caller.
      status,
      stepResults,
      output: workflowResult.output,
      durationMs: workflowResult.totalDurationMs,
    },
  };
}

// ============================================================================
// Memory Recording (Issue #753)
// ============================================================================

/** Records successful workflow execution. Best-effort. */
function recordWorkflowSuccess(template: string, stepsCompleted: number, duration: number): void {
  try {
    const memory = getToolMemory();
    memory.recordTask({
      approach: `Workflow: ${template}`,
      challenges: [],
      durationMs: duration,
    });
    memory.recordLearning({
      pattern: `Workflow ${template} completed in ${String(stepsCompleted)} steps`,
      context: `duration=${String(duration)}ms`,
      confidence: 0.75,
      source: 'run-workflow',
    });
    void memory.runPromotionPipeline().catch((error: unknown) => {
      createLogger({ tool: 'run-workflow' }).warn('Promotion pipeline failed', { error });
    });
  } catch (error: unknown) {
    createLogger({ tool: 'run-workflow' }).warn('Failed to record workflow success', {
      error: getErrorMessage(error),
      template,
    });
  }
}

/** Records workflow execution failure. Best-effort. */
function recordWorkflowError(template: string, errorMessage: string): void {
  try {
    getToolMemory().recordError({
      error: `Workflow ${template}: ${errorMessage.slice(0, 100)}`,
      solution: 'Pending - workflow failed',
      filePattern: 'mcp/tools/run-workflow',
    });
  } catch (error: unknown) {
    createLogger({ tool: 'run-workflow' }).warn('Failed to record workflow error', {
      error: getErrorMessage(error),
      template,
    });
  }
}

function recordWorkflowOutcome(template: string, result: WorkflowToolResult): void {
  if (result.status === 'completed') {
    recordWorkflowSuccess(template, result.stepResults.length, result.durationMs);
    return;
  }
  const reason = result.stepResults.some((step) => step.status === 'failed')
    ? 'did not complete: one or more steps failed'
    : 'did not complete: no step succeeded';
  recordWorkflowError(template, reason);
}

/**
 * Build the failure envelope, threading `executionId` + `durationMs` from
 * the engine-enriched WorkflowError context so timed-out runs are
 * queryable via `query_trace` (#2931). Falls back to the legacy
 * 'unknown'/0 shape only when an upstream path didn't enrich the error.
 */
function buildFailureEnvelope(
  workflowName: string,
  error: { message: string; context?: Record<string, unknown> | undefined }
): ToolResponse {
  const ctx = error.context;
  const executionId = typeof ctx?.['executionId'] === 'string' ? ctx['executionId'] : undefined;
  const durationMs = typeof ctx?.['durationMs'] === 'number' ? ctx['durationMs'] : undefined;
  return createFailedResult(workflowName, error.message, {
    ...(executionId !== undefined ? { executionId } : {}),
    ...(durationMs !== undefined ? { durationMs } : {}),
  });
}

/**
 * Handle tool execution and format response.
 *
 * @param deps - Tool dependencies
 * @param args - Validated tool arguments
 * @returns MCP tool response
 */
async function handleRunWorkflow(
  deps: RunWorkflowDeps,
  args: RunWorkflowInput,
  /** Async-job heartbeat (#6162), fired per settled phase. Absent in sync mode. */
  onPhaseComplete?: () => void
): Promise<ToolResponse> {
  const { template, inputs, dryRun, timeoutMs } = args;
  deps.logger?.debug('run_workflow called', {
    template,
    dryRun,
    inputKeys: Object.keys(inputs),
    ...(timeoutMs !== undefined ? { timeoutMs } : {}),
  });

  const loadResult = await loadWorkflow(deps, template);
  if (!loadResult.ok) {
    return errorResponse(loadResult.error.message);
  }

  const workflow = loadResult.value;

  if (dryRun) {
    return successResponse(executeDryRun(workflow, inputs));
  }

  const validation = validateWorkflowInputs(workflow, inputs);
  if (!validation.valid) {
    return errorResponse(formatValidationErrors(validation));
  }

  // #3017: thread the caller-supplied timeoutMs (if any) through to the
  // workflow engine. Wins over both `workflow.timeout` and the engine's
  // `defaultTimeoutMs` for known-long templates.
  const executeResult = await executeWorkflow(deps, workflow, inputs, {
    ...(timeoutMs !== undefined ? { phaseTimeoutMs: timeoutMs } : {}),
    ...(onPhaseComplete !== undefined ? { onPhaseComplete } : {}),
  });
  if (!executeResult.ok) {
    recordWorkflowError(template, executeResult.error.message);
    return buildFailureEnvelope(workflow.name, executeResult.error);
  }

  recordWorkflowOutcome(template, executeResult.value);
  return successResponse(executeResult.value);
}

/**
 * Input schema for registerTool — the internal schema's shape, not a copy.
 *
 * The hand-written mirror this replaces omitted `idempotencyKey`, so the SDK
 * stripped it, every async dispatch minted a fresh jobId, and the `replay` /
 * `collision` envelopes below could never fire. Same cause as #4969 one tool
 * over: a subset drifts, `.shape` cannot (#4972).
 */
const toolInputSchema = RunWorkflowInputSchema.shape;

/**
 * Dispatch the workflow on a background promise + return a pending
 * envelope. Mirrors `dispatchAsyncOrchestrate` in orchestrate.ts (PR
 * #3048) — same protocol, different runner. Concurrency cap is enforced
 * via `tryAcquire('run_workflow')`; over-cap returns the `busy`
 * envelope synchronously so the caller can back off.
 *
 * Per-phase `timeoutMs` is preserved into the background dispatch
 * (the #3017 override still applies in async mode).
 */
/** Replay envelope for run_workflow idempotency (#3042 Stage 1c). */
function buildRunWorkflowReplayEnvelope(jobId: string): ToolResponse {
  return successResponse({
    status: 'replay',
    jobId,
    pollTool: 'get_job_result',
    note: 'Idempotency key matched a prior dispatch — poll get_job_result for current status.',
  });
}

/** Collision envelope for run_workflow idempotency (#3042 Stage 1c). */
function buildRunWorkflowCollisionEnvelope(existingJobId: string): ToolResponse {
  return errorResponse(
    `Idempotency key already used with different inputs. Existing jobId: ${existingJobId}. Use a fresh key or omit it.`
  );
}

function dispatchAsyncRunWorkflow(deps: RunWorkflowDeps, args: RunWorkflowInput): ToolResponse {
  // #3729: dispatch via the shared `runAsJob` helper — the exact sequence this
  // function used to inline (idempotency → busy-on-cap → pending + register →
  // detached run with complete/failed + release-in-finally → pending
  // envelope). run_workflow's only diffs are the freshJobId and the
  // `ToolResponse`-shaped envelopes (structurally identical to ToolResult but
  // built via successResponse/errorResponse for the workflow path).
  return runAsJob<RunWorkflowInput, ToolResponse, ToolResponse>({
    toolName: 'run_workflow',
    input: args,
    idempotencyKey: args.idempotencyKey,
    freshJobId: () => `job-rw-${randomUUID()}`,
    // `handleRunWorkflow` already encapsulates the full sync path (dry-run +
    // validation + recording); recording its whole envelope as the job result
    // preserves the success/error discriminator + stepResults for polling.
    // #5393: deliberately arity-2 — `handleRunWorkflow` drives
    // `executionEngine.execute`, which has no AbortSignal option, so taking the
    // signal here would flip `signalAccepted` to true with nothing reading it.
    // Threading it needs a phase-boundary gate in the engine first: #6305.
    // #6162: heartbeats by jobId after each settled phase.
    run: (jobId, input) =>
      handleRunWorkflow(deps, input, () => {
        heartbeatJob(jobId);
      }),
    toEnvelope: {
      pending: (jobId) =>
        successResponse({
          status: 'pending',
          jobId,
          pollTool: 'get_job_result',
          note: 'Poll via get_job_result({ jobId }) until status !== "pending".',
        }),
      busy: (retryAfterMs) =>
        successResponse({
          status: 'busy',
          retryAfterMs,
          note: 'Async-mode concurrency cap reached for run_workflow. Retry later or use dispatch: "sync".',
        }),
      replay: buildRunWorkflowReplayEnvelope,
      collision: buildRunWorkflowCollisionEnvelope,
    },
    ...(deps.logger !== undefined ? { logger: deps.logger } : {}),
  });
}

/**
 * Creates the core handler logic for run_workflow tool.
 * Rate limiting is handled by createSecureHandler wrapper.
 *
 * @param deps - Tool dependencies
 * @returns Context-aware handler function
 */
function createRunWorkflowHandler(
  deps: RunWorkflowDeps
): (args: unknown, ctx: HandlerContext) => Promise<ToolResponse> {
  const notifier = deps.notifier ?? NOOP_NOTIFIER;
  return async (args: unknown, ctx: HandlerContext): Promise<ToolResponse> => {
    const validated = RunWorkflowInputSchema.safeParse(args);
    if (!validated.success) {
      const errorMessage = validated.error.issues
        .map((e: { path: PropertyKey[]; message: string }) => `${e.path.join('.')}: ${e.message}`)
        .join(', ');
      return errorResponse(`Validation error: ${errorMessage}`);
    }

    // #4968: `dispatch` is canonical; `mode` is the deprecated alias. A call
    // that sent only `mode` still runs, and says so in `_meta` warnings.
    const dispatch = resolveDispatch(validated.data);
    const modeWarning = deprecatedModeWarning(validated.data);
    ctx.logger.debug('Running workflow', {
      template: validated.data.template,
      dryRun: validated.data.dryRun,
      ...(dispatch !== undefined ? { dispatch } : {}),
    });
    notifier.info('run_workflow', { event: 'workflow_start', template: validated.data.template });
    const startMs = getTimeProvider().now();

    // #3044 / epic #2631 Stage 3 — async dispatch. Returns
    // immediately with a pending envelope or a busy envelope. dryRun
    // is fast enough to stay synchronous regardless of dispatch (no point
    // backgrounding a sub-second validation).
    if (dispatch === 'async' && !validated.data.dryRun) {
      const asyncResult = dispatchAsyncRunWorkflow(deps, validated.data);
      notifier.info('run_workflow', {
        event: 'workflow_dispatched_async',
        template: validated.data.template,
      });
      return withWarnings(asyncResult, [modeWarning]);
    }

    const result = withWarnings(await handleRunWorkflow(deps, validated.data), [modeWarning]);

    // Notify on completion (only for non-error responses)
    if (result.isError !== true) {
      notifier.info('run_workflow', {
        event: 'workflow_complete',
        template: validated.data.template,
        durationMs: getTimeProvider().now() - startMs,
      });
    }

    return result;
  };
}

/**
 * Register the run_workflow tool with an MCP server.
 *
 * Uses createSecureHandler for standardized security middleware (Issue #531).
 * Includes timeout protection for CVE-2026-0621 mitigation (Issue #271).
 *
 * @category MCP
 * @param server - MCP server instance
 * @param deps - Tool dependencies
 */
export function registerRunWorkflowTool(server: McpServer, deps: RunWorkflowDeps): void {
  const logger = deps.logger ?? createLogger({ tool: 'run_workflow' });
  const notifier = deps.notifier ?? createMcpNotifier(server);
  const depsWithNotifier = { ...deps, notifier };

  // Wrap handler with secure handler for rate limiting and request context (Issue #531)
  const secureHandler = createSecureHandler(createRunWorkflowHandler(depsWithNotifier), {
    toolName: 'run_workflow',
    rateLimiter: deps.rateLimiter,
    logger,
  });

  // Wrap with timeout protection (Issue #271, CVE-2026-0621)
  const timeoutMs = getToolTimeout('run_workflow', deps.security);
  const wrappedHandler = wrapToolWithTimeout('run_workflow', secureHandler, { timeoutMs, logger });

  server.registerTool(
    'run_workflow',
    {
      description:
        "Run a LINEAR (single-path) workflow template by name with typed inputs. For DAG-shaped workflows with branching, checkpoints, or rollback, use `run_graph_workflow` instead. Supports dispatch: 'async' (non-dryRun runs; `mode` is a deprecated alias) — returns a jobId immediately; poll get_job_result.",
      inputSchema: toolInputSchema,

      annotations: getToolAnnotations('run_workflow'),
    },
    toSdkCallbackWithBudgetCheck(wrappedHandler, 'run_workflow', timeoutMs, logger)
  );
  logger.info('Registered run_workflow tool with secure handler and timeout protection');
}

/**
 * Creates a mock workflow engine for testing purposes only.
 *
 * WARNING: This is a test stub that returns errors for all operations except
 * listTemplates(). Do NOT use in production - use createRealWorkflowEngine()
 * from workflows/workflow-engine-factory.ts instead.
 *
 * This mock is useful for:
 * - Unit testing MCP tools without a real workflow engine
 * - Integration tests that need a predictable workflow engine
 *
 * @returns IWorkflowEngine stub that fails execute/load operations
 * @see createRealWorkflowEngine for production usage
 */
export function createMockWorkflowEngine(): IWorkflowEngine {
  return {
    loadTemplate(path: string) {
      return Promise.resolve({
        ok: false as const,
        error: new ParseError(`Mock workflow engine cannot load templates. Path: ${path}`),
      });
    },

    execute(workflow) {
      return Promise.resolve({
        ok: false as const,
        error: new WorkflowError(`Mock workflow engine cannot execute workflows`, {
          context: { workflowName: workflow.name },
        }),
      });
    },

    getStatus(_executionId: string) {
      return { state: 'pending' as const };
    },

    cancel(_executionId: string) {
      return Promise.resolve({
        ok: false as const,
        error: new WorkflowError('Mock workflow engine cannot cancel executions'),
      });
    },

    /**
     * Returns empty list - mock engine has no templates.
     * (Source: Issue #536 - Consistent behavior with execute/load failures)
     */
    listTemplates() {
      return Promise.resolve([]);
    },

    getTemplateByName() {
      return Promise.resolve(undefined);
    },
  };
}
